import React from "react";
import Icon from "@mdi/react";
import {mdiHistory} from "@mdi/js";
import {b2bProductUrl, b2cProductUrl, weclappArticleUrl} from "../functions/shopUrls.js";

/**
 * Artikelnummer mit den Direktlinks in die drei Systeme darunter
 * (B2C, B2B, Weclapp) und optional dem Verlaufs-Symbol.
 *
 * Bewusst eine eigene Komponente: dieselbe Zelle erscheint in der
 * Lagerübersicht UND im Änderungs-Log. Als Kopie in beiden Seiten wäre
 * garantiert, dass die eine irgendwann geändert und die andere vergessen
 * wird - genau die Art von Regression, gegen die die Funktionsinventur
 * geführt wird.
 *
 * Sichtbarkeit der Schaltflächen: B2C, wenn eine WooCommerce-Produkt-ID
 * hinterlegt ist (Artikel steht in der Produktliste); B2B, wenn der
 * Statusimport den Artikel im OpenMage-Shop gefunden hat (b2b_status ist
 * dann gesetzt). Die Weclapp-Schaltfläche erscheint immer - notfalls als
 * Rückfall auf die Artikelliste (siehe weclappArticleUrl).
 *
 * @param {string} sku - Artikelnummer.
 * @param {number|null} b2cDeId - WooCommerce-Produkt-ID oder null.
 * @param {string|null} b2bStatus - Verfügbarkeitsstatus im B2B-Shop oder null.
 * @param {number|null} weclappId - Interne Weclapp-Artikel-ID oder null.
 * @param {(sku: string) => void} [onShowHistory] - Ohne Angabe entfällt das Verlaufs-Symbol.
 */
export default function ArticleShopLinks({sku, b2cDeId, b2bStatus, weclappId, onShowHistory}) {
    const b2cExists = b2cDeId !== null && b2cDeId !== undefined;
    const b2bExists = b2bStatus !== null && b2bStatus !== undefined;

    const badgeClass = "badge bg-light text-secondary border text-decoration-none";
    const badgeStyle = {fontSize: "0.6rem"};

    return (
        <div className="d-flex flex-column align-items-start">
            <span>{sku}</span>
            <span className="d-flex flex-wrap gap-1 mt-1">
                {b2cExists && (
                    <a
                        href={b2cProductUrl(sku, b2cDeId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={badgeClass}
                        style={badgeStyle}
                        title="Im B2C-Shop öffnen (shop.molotow.com)"
                    >
                        B2C
                    </a>
                )}
                {b2bExists && (
                    <a
                        href={b2bProductUrl(sku)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={badgeClass}
                        style={badgeStyle}
                        title="Im B2B-Shop öffnen (b2b.molotow.com)"
                    >
                        B2B
                    </a>
                )}
                <a
                    href={weclappArticleUrl(sku, weclappId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={badgeClass}
                    style={badgeStyle}
                    title={weclappId ? "In Weclapp öffnen" : "In Weclapp suchen (keine direkte Artikel-ID bekannt)"}
                >
                    Weclapp
                </a>
                {onShowHistory && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={() => onShowHistory(sku)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onShowHistory(sku);
                            }
                        }}
                        className={`${badgeClass} d-inline-flex align-items-center`}
                        style={{...badgeStyle, cursor: "pointer"}}
                        title="Änderungsverlauf dieses Artikels anzeigen"
                        aria-label={`Änderungsverlauf von ${sku} anzeigen`}
                    >
                        <Icon path={mdiHistory} size={0.45} />
                    </span>
                )}
            </span>
        </div>
    );
}
